import React from 'react';
import dayjs from 'dayjs';
import { PrintFooter, printStyles } from './PrintBase';

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const STATUS_LABELS = {
  creada: 'Pendiente de Autorización',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  entregada: 'Entregada',
  cancelada: 'Cancelada'
};

const ESTADO_RECEPCION_LABELS = {
  ok: 'Bien',
  dañado: 'Dañado',
  faltante: 'Faltante',
  otro: 'Otro problema'
};

const ESTADO_PAGO_LABELS = {
  no_pagado: 'No pagado',
  parcial: 'Pago parcial',
  pagado: 'Pagado'
};

const METODO_PAGO_LABELS = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  tarjeta: 'Tarjeta',
  otro: 'Otro'
};

const OrdenCompraPrint = React.forwardRef(function OrdenCompraPrint({ orden }, ref) {
  if (!orden) return <div ref={ref} />;

  const detalles = orden.detalles || [];
  const proveedor = orden.proveedor || {};
  const mostrarRecepcion = orden.status === 'entregada';
  const detallesConIncidencia = detalles.filter((d) => d.estado_recepcion && d.estado_recepcion !== 'ok');

  return (
    <div ref={ref} style={printStyles.page}>
      {/* Encabezado tipo Nota de Remisión */}
      <div style={printStyles.header}>
        <div>
          <img src="/images/logoOf.png" alt="RADTRANS" style={{ height: '32pt' }} />
          {orden.codigo && (
            <p style={{ ...printStyles.dateLabel, fontSize: '9pt', margin: '2pt 0 0 0' }}>
              <strong>{orden.codigo}</strong>
            </p>
          )}
        </div>
        <div style={printStyles.dateBlock}>
          <p style={{ ...printStyles.dateLabel, fontSize: '9pt' }}>ORDEN DE COMPRA</p>
          <p style={{ ...printStyles.dateValue, fontSize: '16pt', color: '#1677ff' }}>
            {orden.numero_orden}
          </p>
        </div>
      </div>

      <h2 style={printStyles.title}>Orden de Compra</h2>
      <p style={printStyles.subtitle}>
        Estado: <strong>{STATUS_LABELS[orden.status] || orden.status}</strong>
        {orden.tiene_incidencia && (
          <span style={{ color: '#d4380d', fontWeight: 'bold' }}> — CON INCIDENCIAS EN LA ENTREGA</span>
        )}
      </p>

      {/* Datos generales */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12pt' }}>
        <tbody>
          <tr>
            <td style={{ ...printStyles.td, width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4pt', fontSize: '9.5pt' }}>PROVEEDOR</div>
              <div style={{ fontSize: '9pt' }}>
                <div><strong>{proveedor.nombre_comercial}</strong> {proveedor.codigo ? `(${proveedor.codigo})` : ''}</div>
                <div>Contacto: {proveedor.contacto_nombre || '—'}</div>
                <div>Teléfono: {proveedor.contacto_telefono || proveedor.telefono || '—'}</div>
                <div>Email: {proveedor.contacto_email || proveedor.email || '—'}</div>
                <div>Dirección: {proveedor.direccion || '—'}</div>
              </div>
            </td>
            <td style={{ ...printStyles.td, width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4pt', fontSize: '9.5pt' }}>DATOS DE LA ORDEN</div>
              <div style={{ fontSize: '9pt' }}>
                <div>Fecha de pedido: <strong>{orden.fecha_pedido ? dayjs(orden.fecha_pedido).format('DD/MM/YYYY') : '—'}</strong></div>
                <div>Fecha de entrega estimada: <strong>{orden.fecha_entrega_estimada ? dayjs(orden.fecha_entrega_estimada).format('DD/MM/YYYY') : '—'}</strong></div>
                {orden.fecha_entrega_real && (
                  <div>Fecha de entrega confirmada: <strong>{dayjs(orden.fecha_entrega_real).format('DD/MM/YYYY HH:mm')}</strong></div>
                )}
                <div>Método de envío: {orden.metodo_envio || '—'}</div>
                {orden.direccion_entrega && (
                  <div>Dirección de entrega: {orden.direccion_entrega}</div>
                )}
                <div>Creada por: {orden.creador?.nombre || '—'}</div>
                {orden.autorizador?.nombre && <div>Autorizada por: {orden.autorizador.nombre}</div>}
                {orden.entregador?.nombre && <div>Entrega confirmada por: {orden.entregador.nombre}</div>}
                <div>
                  Estado de pago: <strong>{ESTADO_PAGO_LABELS[orden.estado_pago] || 'No pagado'}</strong>
                  {(orden.estado_pago || 'no_pagado') !== 'no_pagado' &&
                    ` (${formatCurrency(orden.monto_pagado)} de ${formatCurrency(orden.total)})`}
                </div>
                {orden.fecha_limite_pago && (
                  <div>Fecha límite de pago: <strong>{dayjs(orden.fecha_limite_pago).format('DD/MM/YYYY')}</strong></div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Detalle de productos */}
      <div style={printStyles.sectionTitle}>Detalle de Productos</div>
      <table style={printStyles.table}>
        <thead>
          <tr>
            <th style={printStyles.th}>Clave</th>
            <th style={printStyles.th}>Producto</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Cantidad</th>
            {mostrarRecepcion && (
              <>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Recibido</th>
                <th style={printStyles.th}>Estado</th>
              </>
            )}
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Costo Unit.</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Subtotal</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>IVA %</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>IVA</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {detalles.map((d) => (
            <tr key={d.id}>
              <td style={printStyles.td}>{d.producto?.clave || '—'}</td>
              <td style={printStyles.td}>{d.producto?.nombre || '—'}</td>
              <td style={printStyles.tdRight}>
                {parseFloat(d.cantidad).toLocaleString('es-MX')} {d.producto?.unidad_medida || ''}
              </td>
              {mostrarRecepcion && (
                <>
                  <td style={printStyles.tdRight}>
                    {d.cantidad_recibida != null ? parseFloat(d.cantidad_recibida).toLocaleString('es-MX') : '—'} {d.producto?.unidad_medida || ''}
                  </td>
                  <td style={{
                    ...printStyles.td,
                    color: d.estado_recepcion && d.estado_recepcion !== 'ok' ? '#d4380d' : '#237804',
                    fontWeight: 'bold'
                  }}>
                    {ESTADO_RECEPCION_LABELS[d.estado_recepcion] || '—'}
                  </td>
                </>
              )}
              <td style={printStyles.tdRight}>{formatCurrency(d.costo_unitario)}</td>
              <td style={printStyles.tdRight}>{formatCurrency(d.subtotal)}</td>
              <td style={printStyles.tdRight}>{parseFloat(d.iva_porcentaje).toLocaleString('es-MX')}%</td>
              <td style={printStyles.tdRight}>{formatCurrency(d.iva_monto)}</td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>{formatCurrency(d.total)}</td>
            </tr>
          ))}
          {detalles.length === 0 && (
            <tr>
              <td colSpan={mostrarRecepcion ? 10 : 8} style={{ ...printStyles.tdCenter, color: '#888', fontStyle: 'italic' }}>
                Sin productos
              </td>
            </tr>
          )}
        </tbody>
        {detalles.length > 0 && (
          <tfoot>
            <tr style={printStyles.trSummary}>
              <td colSpan={mostrarRecepcion ? 6 : 4} style={{ ...printStyles.td, fontWeight: 'bold' }}>TOTALES</td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>{formatCurrency(orden.subtotal)}</td>
              <td style={printStyles.tdRight} />
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>{formatCurrency(orden.iva_total)}</td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', fontSize: '10pt' }}>{formatCurrency(orden.total)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {detallesConIncidencia.length > 0 && (
        <>
          <div style={{ ...printStyles.sectionTitle, color: '#d4380d' }}>Incidencias Reportadas en la Entrega</div>
          <table style={printStyles.table}>
            <thead>
              <tr>
                <th style={printStyles.th}>Producto</th>
                <th style={printStyles.th}>Estado</th>
                <th style={printStyles.th}>Detalle reportado</th>
                <th style={printStyles.th}>Resolución</th>
              </tr>
            </thead>
            <tbody>
              {detallesConIncidencia.map((d) => (
                <tr key={d.id}>
                  <td style={printStyles.td}>{d.producto?.nombre || '—'}</td>
                  <td style={{ ...printStyles.td, color: '#d4380d', fontWeight: 'bold' }}>
                    {ESTADO_RECEPCION_LABELS[d.estado_recepcion] || d.estado_recepcion}
                  </td>
                  <td style={printStyles.td}>{d.nota_recepcion || '—'}</td>
                  <td style={printStyles.td}>
                    {d.incidencia_resuelta ? (
                      <span style={{ color: '#237804', fontWeight: 'bold' }}>
                        Resuelta: {d.nota_resolucion || '—'}
                      </span>
                    ) : (
                      <span style={{ color: '#d4380d', fontWeight: 'bold' }}>Pendiente</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {orden.pagos?.length > 0 && (
        <>
          <div style={printStyles.sectionTitle}>Historial de Pagos</div>
          <table style={printStyles.table}>
            <thead>
              <tr>
                <th style={printStyles.th}>Fecha</th>
                <th style={{ ...printStyles.th, textAlign: 'right' }}>Monto</th>
                <th style={printStyles.th}>Método</th>
                <th style={printStyles.th}>Referencia</th>
                <th style={printStyles.th}>Nota</th>
              </tr>
            </thead>
            <tbody>
              {orden.pagos.map((p) => (
                <tr key={p.id}>
                  <td style={printStyles.td}>{dayjs(p.fecha_pago).format('DD/MM/YYYY')}</td>
                  <td style={printStyles.tdRight}>{formatCurrency(p.monto)}</td>
                  <td style={printStyles.td}>{METODO_PAGO_LABELS[p.metodo_pago] || p.metodo_pago}</td>
                  <td style={printStyles.td}>{p.referencia || '—'}</td>
                  <td style={printStyles.td}>{p.nota || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {orden.notas_entrega && (
        <>
          <div style={printStyles.sectionTitle}>Notas de la Entrega</div>
          <p style={{ fontSize: '9pt' }}>{orden.notas_entrega}</p>
        </>
      )}

      {orden.notas && (
        <>
          <div style={printStyles.sectionTitle}>Notas</div>
          <p style={{ fontSize: '9pt' }}>{orden.notas}</p>
        </>
      )}

      {/* Firmas */}
      <div style={{ display: 'flex', gap: '24pt', marginTop: '30pt' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '9pt', fontWeight: 'bold', marginBottom: '2pt' }}>
            {orden.creador?.nombre || ' '}
          </div>
          <div style={{ borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
            Elaboró
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '9pt', fontWeight: 'bold', marginBottom: '2pt' }}>
            {orden.autorizador?.nombre || ' '}
          </div>
          <div style={{ borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
            Autorizó (Gerencia)
          </div>
        </div>
        {/*
        <div style={{ flex: 1, textAlign: 'center', borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
          Recibió (Proveedor)
        </div>
        */}
      </div>

      <PrintFooter />
    </div>
  );
});

export default OrdenCompraPrint;
